/**
 * Client-facing label helpers (MER-179).
 *
 * Source data flows through canonical snake_case identifiers
 * (`equipment_type`, `asl`, `front_load`, etc). Those reach the
 * dashboard verbatim from PostgREST and would render as nonsense to
 * the customer (`EQUIPMENT_TYPE`, `asl`) without a transform.
 *
 * `humanize` is the single entry point. Every client-visible string
 * derived from a canonical identifier should go through this helper.
 * Filter dropdown values keep the snake_case key for the query layer;
 * only the rendered text changes.
 *
 * Add overrides for terms where vanilla title-casing is wrong (acronyms,
 * synonyms, special characters). The data layer must keep using the
 * original canonical key. See `OVERRIDES` for the running list.
 */

const OVERRIDES: Record<string, string> = {
  // Equipment / service synonyms. `asl` is the canonical Side Load
  // identifier. Memory: asl-side-load-synonym.
  asl: "Side Load",
  side_load: "Side Load",
  front_load: "Front Load",
  rear_load: "Rear Load",
  roll_off: "Roll Off",
  hook_lift: "Hook Lift",
  vacuum_truck: "Vacuum Truck",
  // Common acronyms / proper terms.
  "p&l": "P&L",
  ebitda: "EBITDA",
  kpi: "KPI",
  ar: "AR",
  ap: "AP",
  dso: "DSO",
  emr: "EMR",
  mtd: "MTD",
  wtd: "WTD",
  ytd: "YTD",
  lob: "LOB",
  fl: "FL",
  ro: "RO",
  sl: "SL",
  rl: "RL",
  msw: "MSW",
  fel: "FEL",
  // Material types worth the override.
  c_and_d: "C&D",
  e_waste: "E-Waste",
};

function titleCaseWord(word: string): string {
  if (word.length === 0) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Render a snake_case / lower-cased identifier as a client-safe label.
 *
 * Lookup order:
 *   1. Exact override on the lowercased input (`asl` -> `Side Load`).
 *   2. Per-word override after splitting on `_` (`asl_truck` -> `Side Load Truck`).
 *   3. Title Case fallback with `_` replaced by space.
 *
 * Idempotent and tolerant of already-humanized input: a string with no
 * underscores and at least one uppercase letter passes through unchanged
 * so manifest-authored labels ("Disposal Fee Recovery", "P&L") are not
 * mangled.
 */
export function humanize(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  if (s.length === 0) return "";
  const lower = s.toLowerCase();
  const exact = OVERRIDES[lower];
  if (exact) return exact;
  // Pre-humanized strings (e.g. manifest names) pass through.
  if (!s.includes("_") && /[A-Z]/.test(s)) return s;
  const parts = lower.split("_");
  return parts.map((p) => OVERRIDES[p] ?? titleCaseWord(p)).join(" ");
}
